/**
 * =============================================================================
 * PUENTE DE AUTENTICACIÓN — un ejemplo de INVERSIÓN DE DEPENDENCIAS
 * =============================================================================
 *
 * EL PROBLEMA:
 * El cliente HTTP (que vive en `shared/`) necesita el token de sesión para
 * ponerlo en la cabecera Authorization de cada petición. Pero el token lo
 * maneja la feature `auth`. Entonces `shared/api` tendría que importar de
 * `features/auth`... lo cual rompe la regla de dependencias del proyecto
 * (shared no puede conocer el negocio) y ESLint lo rechazaría.
 *
 * ¿Y por qué es una regla y no un capricho? Porque si `shared` importara de
 * `features`, se crearía un ciclo: auth usa el cliente HTTP para hacer login,
 * y el cliente HTTP usa auth para el token. Los ciclos rompen el orden de
 * carga de los módulos y producen errores de "undefined" imposibles de leer.
 *
 * LA SOLUCIÓN — inversión de dependencias:
 * En vez de que `shared` vaya a buscar el token, `shared` define aquí un hueco
 * (un "puerto") y la feature `auth` lo rellena al arrancar la aplicación.
 * La dependencia apunta hacia abajo: auth → shared. Nunca al revés.
 *
 * Es el mismo principio de un enchufe: la pared no sabe qué aparato conectarás,
 * solo define la forma del contacto.
 * =============================================================================
 */

interface AuthBridge {
  /** Devuelve el token vigente, o null si no hay sesión. */
  getToken: () => string | null
  /** Se llama cuando el backend responde 401: la sesión ya no sirve. */
  onSessionExpired: () => void
}

/** Implementación por defecto: no hay sesión y no pasa nada al expirar. */
let bridge: AuthBridge = {
  getToken: () => null,
  onSessionExpired: () => {},
}

/**
 * La feature `auth` llama a esta función una sola vez, al iniciar la app,
 * para conectar su lógica de sesión con el cliente HTTP.
 *
 * @see src/features/auth/store/authStore.ts
 */
export function connectAuth(implementation: AuthBridge): void {
  bridge = implementation
}

/** Uso interno del cliente HTTP. */
export function getCurrentToken(): string | null {
  return bridge.getToken()
}

/** Uso interno del cliente HTTP. */
export function notifySessionExpired(): void {
  bridge.onSessionExpired()
}
